
import type { ListingAddressInput } from './inputs.ts';

export type Address = ListingAddressInput;

export type AddressDraft = Partial<Omit<Address, 'formatted'>> & { formatted: string };

export interface AddressNullable {
  formatted: string;
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
  placeId?: string | null;
  lat?: number | null;
  lng?: number | null;
  timezone: string;
}

export const COMPONENT_ORDER = [
  'line1',
  'line2',
  'city',
  'region',
  'postalCode',
  'country',
] as const;

export type AddressComponent = (typeof COMPONENT_ORDER)[number];

export function compose(a: AddressDraft): string {
  return COMPONENT_ORDER.map((k) => a[k])
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .join(', ');
}

/** The listing's display title: full formatted address, else street lines, else a placeholder. */
export function addressTitle(a: {
  formatted?: string | null;
  line1?: string | null;
  line2?: string | null;
}): string {
  if (a.formatted && a.formatted.trim() !== '') return a.formatted;
  const parts = [a.line1, a.line2].filter((p): p is string => Boolean(p && p.trim()));
  return parts.length > 0 ? parts.join(', ') : 'Untitled listing';
}

export function hasStructured(a: AddressDraft): boolean {
  return COMPONENT_ORDER.some((k) => {
    const v = a[k];
    return typeof v === 'string' && v.trim() !== '';
  });
}

export function equals(a: AddressDraft, b: AddressDraft): boolean {
  const eq = (x?: string, y?: string) => (x ?? '').trim() === (y ?? '').trim();
  return (
    eq(a.formatted, b.formatted) &&
    COMPONENT_ORDER.every((k) => eq(a[k], b[k])) &&
    eq(a.placeId, b.placeId) &&
    a.lat === b.lat &&
    a.lng === b.lng &&
    eq(a.timezone, b.timezone)
  );
}

export function toNullable(a: Address): AddressNullable {
  return {
    formatted: a.formatted,
    line1: a.line1 ?? null,
    line2: a.line2 ?? null,
    city: a.city ?? null,
    region: a.region ?? null,
    postalCode: a.postalCode ?? null,
    country: a.country ?? null,
    placeId: a.placeId ?? null,
    lat: a.lat ?? null,
    lng: a.lng ?? null,
    timezone: a.timezone,
  };
}

export function toOptional(a: AddressNullable): Address {
  return {
    formatted: a.formatted,
    line1: a.line1 ?? undefined,
    line2: a.line2 ?? undefined,
    city: a.city ?? undefined,
    region: a.region ?? undefined,
    postalCode: a.postalCode ?? undefined,
    country: a.country ?? undefined,
    placeId: a.placeId ?? undefined,
    lat: a.lat ?? undefined,
    lng: a.lng ?? undefined,
    timezone: a.timezone,
  };
}
